// Phase APP-2 · Approval notifications bell (top-bar widget).
// Polls /api/approvals/notifications?unread_only=true every 30s.
// Renders alongside the existing legacy NotificationBell — this one is
// scoped to APP-1 approval events.
import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, CheckCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../../../services/api";
import useAuthStore from "../../../store/authStore";

const ORANGE = "#FF6B00";
const ELIGIBLE_ROLES = new Set([
  "admin", "main_hub_manager", "micro_hub_lead", "founder",
]);

function relTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function roleDeepLink(role, requestId) {
  if (role === "admin") return `/dashboard/admin/approvals?id=${requestId}`;
  if (role === "main_hub_manager") return `/dashboard/hub/packer-approvals?id=${requestId}`;
  if (role === "micro_hub_lead") return `/dashboard/micro-hub/worker-requests?id=${requestId}`;
  if (role === "founder") return `/dashboard/founder/oversight?id=${requestId}`;
  return "/";
}

export default function ApprovalNotificationBell() {
  const user = useAuthStore((s) => s.user);
  const role = (user?.role || "").toLowerCase();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const ref = useRef(null);

  const load = useCallback(async () => {
    if (!ELIGIBLE_ROLES.has(role)) return;
    try {
      const r = await api.get("/approvals/notifications", { params: { limit: 50 } });
      const all = r.data?.rows || [];
      setRows(all);
      setUnreadCount(all.filter((n) => !n.read).length);
    } catch {
      /* swallow — bell is non-critical */
    }
  }, [role]);

  useEffect(() => {
    if (!ELIGIBLE_ROLES.has(role)) return undefined;
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [role, load]);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markOne = async (e, id) => {
    e?.stopPropagation();
    try {
      await api.post(`/approvals/notifications/${id}/mark-read`);
      setRows((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch { /* noop */ }
  };
  const markAll = async () => {
    try {
      await api.post("/approvals/notifications/mark-all-read");
      setRows((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* noop */ }
  };
  const onRowClick = (n) => {
    setOpen(false);
    if (!n.read) markOne(null, n.id);
    navigate(roleDeepLink(role, n.approval_request_id));
  };

  if (!ELIGIBLE_ROLES.has(role)) return null;
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((v) => !v)}
              aria-label="Approval notifications"
              data-testid="approval-bell"
              data-unread={unreadCount > 0 ? "true" : "false"}
              className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors">
        <Bell className="h-5 w-5 text-slate-600"/>
        {unreadCount > 0 && (
          <span data-testid="approval-bell-badge"
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-[#FF6B00] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-12 w-80 sm:w-96 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden z-50"
             data-testid="approval-bell-panel">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50">
            <span className="text-sm font-semibold">Approvals</span>
            <button onClick={markAll}
                    data-testid="approval-bell-mark-all"
                    disabled={unreadCount === 0}
                    className="text-[11px] inline-flex items-center gap-1 text-[#C2410C] hover:underline disabled:opacity-40">
              <CheckCheck className="h-3 w-3"/> Mark all read
            </button>
          </div>
          {rows.length === 0
            ? <div className="px-4 py-6 text-center text-xs text-slate-400" data-testid="approval-bell-empty">
                No new notifications
              </div>
            : (
              <ul className="max-h-96 overflow-y-auto divide-y divide-slate-100">
                {rows.slice(0, 30).map((n, i) => (
                  <li key={n.id || i}
                      data-testid={`approval-bell-row-${i}`}
                      onClick={() => onRowClick(n)}
                      className={"px-3 py-2 text-xs cursor-pointer hover:bg-orange-50/40 " +
                                  (n.read ? "opacity-70" : "")}>
                    <div className="flex items-start gap-2">
                      <span className={"w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 " +
                                          (n.read ? "bg-slate-300" : "bg-[#FF6B00]")}/>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-800 truncate">{n.title}</div>
                        <div className="text-slate-500 truncate">{n.body}</div>
                        <div className="text-[10px] text-slate-400">
                          {relTime(n.ts)} · {n.approval_type}
                        </div>
                      </div>
                      {!n.read && (
                        <button onClick={(e) => markOne(e, n.id)}
                                data-testid={`approval-bell-mark-${i}`}
                                className="shrink-0 text-[10px] text-[#C2410C] hover:underline">
                          Mark read
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
        </div>
      )}
    </div>
  );
}
